import { liquityBranchesByChain, liquityConfigFor } from '@1delta/data-sdk'
import { LIQUITY_FAMILY_LENDERS } from '@1delta/lender-registry'
import { LiquityPriceFeedAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import {
  liquityCollateralPrice,
  liquityLenderKey,
} from '../../../lending/public-data/liquity/convertPublic'

/**
 * Liquity-family oracle price fetcher (Liquity V2 + friendly forks: Felix,
 * USDaf, Nerite, Quill, Enosys, Soneta, Ebisu).
 *
 * Each branch's `PriceFeed.lastGoodPrice()` returns the COLLATERAL price in USD
 * (1e18-scaled on canonical 18-dec collateral; see `liquityCollateralPrice` for
 * the fork-dependent scale on non-18-dec branches) — the same value the branch
 * uses for its collateral ratio, and the same read the public-data fetcher
 * already makes. So these are DIRECT USD prices (PASS 1) that feed the shared
 * `usdPrices` map.
 *
 * Why a dedicated fetcher (not cross-protocol flattening): the family spans
 * chains where no other integrated protocol prices the collateral (Felix/HYPE
 * on HyperEVM, Enosys on Flare, Soneta on Sonic, Ebisu on Plasma, plus bespoke
 * LST/stable collateral on mainnet). Without this those branches would serve a
 * null oracle price. It also uses each branch's OWN feed — the correct oracle
 * for risk. `lastGoodPrice` is a view (fresh on any active branch), so one call
 * per branch. The stablecoin (BOLD / USDaf / satUSD-like) is the debt asset and
 * is emitted pegged to $1 (the same peg `convertPublic` uses) so debt is priced.
 */

interface LiquityBranchMeta {
  lender: string
  chainId: string
  collIndex: number
  collToken: string
  /** Collateral token decimals — sets the feed scale (see below). */
  collDecimals: number
  /** Feed decimals from metadata when known; auto-detected otherwise. */
  priceDecimals?: number
  /** Stable/debt token (pegged to 1). Emitted once per branch. */
  debtToken?: string
}

/** Build one `lastGoodPrice()` call per branch across every family deployment. */
export function getLiquityCalls(
  chainId: string,
): FetcherResult<LiquityBranchMeta>[] {
  const results: FetcherResult<LiquityBranchMeta>[] = []

  for (const lender of LIQUITY_FAMILY_LENDERS) {
    const branches = liquityBranchesByChain(lender, chainId)
    if (branches.length === 0) continue
    const debtToken = liquityConfigFor(lender, chainId)?.boldToken

    for (const b of branches) {
      if (!b.priceFeed || !b.collToken) continue
      const calls: MulticallCall[] = [
        { address: b.priceFeed, name: 'lastGoodPrice', params: [] },
      ]
      results.push({
        calls,
        meta: {
          lender,
          chainId,
          collIndex: b.collIndex,
          collToken: b.collToken.toLowerCase(),
          collDecimals: b.collDecimals,
          priceDecimals: b.priceDecimals,
          debtToken: debtToken?.toLowerCase(),
        },
        lender: liquityLenderKey(lender, chainId, b.collIndex),
      })
    }
  }

  return results
}

/**
 * Parse a branch's `lastGoodPrice` into direct-USD entries: the collateral at
 * its on-chain USD price, and the stablecoin pegged to $1. Both keyed under the
 * per-branch `<DEPLOYMENT>_<chain>_<collIndex>` lender so the marketUids join
 * the Liquity lending data.
 */
export function parseLiquityResults(
  data: any[],
  meta: LiquityBranchMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = liquityLenderKey(meta.lender, chainId, meta.collIndex)
  const entries: OraclePriceEntry[] = []

  const raw = data?.[0]
  const price =
    typeof raw === 'bigint'
      ? raw
      : raw != null && raw !== '0x'
        ? BigInt(raw)
        : 0n
  // `lastGoodPrice` is 1e18-USD on canonical 18-dec collateral, but forks that
  // list non-18-dec collateral disagree on where the scaling lives — Ebisu bakes
  // it into the feed (USD × 10^(36-collDec)), Enosys keeps the feed at 1e18 and
  // normalizes collateral in its CR math. `liquityCollateralPrice` resolves that
  // per branch (metadata first, plausibility auto-detect otherwise) and returns
  // 0 when neither convention yields a sane price — that branch's collateral
  // then falls back to another protocol's price via asset-group flattening.
  const collUSD = liquityCollateralPrice(
    price,
    meta.collDecimals,
    meta.priceDecimals,
  )
  if (collUSD > 0) {
    entries.push({
      asset: meta.collToken,
      price: collUSD,
      priceUSD: collUSD,
      marketUid: createMarketUid(chainId, key, meta.collToken),
      targetLender: key,
      description: 'Liquity branch PriceFeed (USD)',
    })
  }

  // Stablecoin debt asset — redeemable at par; peg to $1 (matches convertPublic)
  // so a branch whose stable no other protocol prices still has a debt price.
  if (meta.debtToken) {
    entries.push({
      asset: meta.debtToken,
      price: 1,
      priceUSD: 1,
      marketUid: createMarketUid(chainId, key, meta.debtToken),
      targetLender: key,
      description: 'Liquity stablecoin (par)',
      staticBase: true,
      baseAsset: meta.debtToken,
    })
  }

  return entries
}

/** ABI for the Liquity oracle call (`lastGoodPrice`). */
export function getLiquityAbi() {
  return LiquityPriceFeedAbi
}

/** Liquity-family Oracle Fetcher module. */
export const liquityFetcher = {
  getCalls: getLiquityCalls,
  parse: parseLiquityResults,
  getAbi: getLiquityAbi,
}
